import { useState, useMemo } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { filesCloud, timestamp } from "../../firebase/config";
import Select from "react-select";

export default function RichiesteInfoDocumenti() {
  const { user } = useAuthContext();
  const { documents: richieste, error } = useCollection("richiesteInfo", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: settoriDocs } = useCollection("settori");
  const { addDocument, updateDocument, deleteDocument } =
    useFirestore("richiesteInfo");

  const [dataFattura, setDataFattura] = useState("");
  const [nomeFornitore, setNomeFornitore] = useState("");
  const [importo, setImporto] = useState("");
  const [note, setNote] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [settoriSelezionati, setSettoriSelezionati] = useState([]);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [reopeningTicketId, setReopeningTicketId] = useState(null);
  const [reopenSettoriSelezionati, setReopenSettoriSelezionati] = useState({});

  const settoreOptions = useMemo(() => {
    if (!settoriDocs) return [];
    return settoriDocs.map((s) => ({
      value: s.id,
      label: s.nome || s.id,
    }));
  }, [settoriDocs]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setScreenshot(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!dataFattura) {
      setFormError("Inserisci la data della fattura");
      return;
    }
    if (!nomeFornitore.trim()) {
      setFormError("Inserisci il nome del fornitore");
      return;
    }
    if (!importo) {
      setFormError("Inserisci l'importo");
      return;
    }
    if (!settoriSelezionati.length) {
      setFormError("Seleziona almeno un settore");
      return;
    }
    if (!screenshot) {
      setFormError("Carica uno screenshot della fattura");
      return;
    }

    setIsSaving(true);

    try {
      const uploadPath = `richiesteInfo/${user.uid}/${screenshot.name}_${Date.now()}`;
      const uploadRef = await filesCloud.ref(uploadPath).put(screenshot);
      const fotoUrl = await uploadRef.ref.getDownloadURL();

      const richiesta = {
        data: timestamp.fromDate(new Date(dataFattura)),
        nomeFornitore: nomeFornitore.trim(),
        importo: parseFloat(importo),
        note: note.trim(),
        fotoUrl,
        status: "pending",
        settori: settoriSelezionati.map((s) => ({
          id: s.value,
          nome: s.label,
          status: "pending",
        })),
        createdAt: timestamp.fromDate(new Date()),
      };

      await addDocument(richiesta);

      setDataFattura("");
      setNomeFornitore("");
      setImporto("");
      setNote("");
      setScreenshot(null);
      setSettoriSelezionati([]);
    } catch (err) {
      console.error(err);
      setFormError("Errore durante il salvataggio della richiesta");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (richiesta) => {
    if (richiesta.status === "pending") {
      await updateDocument(richiesta.id, { status: "closed" });
    }
  };

  const handleDeleteTicket = async (richiesta) => {
    const confirmDelete = window.confirm(
      "Sei sicuro di voler eliminare definitivamente questo ticket?",
    );
    if (!confirmDelete) return;
    await deleteDocument(richiesta.id);
  };

  const handleConfirmReopen = async (richiesta) => {
    const selected = reopenSettoriSelezionati[richiesta.id] || [];
    if (
      !selected.length ||
      !richiesta.settori ||
      !Array.isArray(richiesta.settori)
    ) {
      return;
    }

    const idsToReopen = selected.map((s) => s.value);

    const updatedSettori = richiesta.settori.map((s) =>
      idsToReopen.includes(s.id) ? { ...s, status: "pending" } : s,
    );

    const anyPending = updatedSettori.some((s) => s.status === "pending");

    const updates = {
      settori: updatedSettori,
      status: anyPending ? "pending" : richiesta.status,
    };

    if (anyPending) {
      updates.projectId = null;
      updates.projectNome = null;
      updates.projectCodice = null;
      updates.assignedSectorId = null;
      updates.assignedSectorNome = null;
      updates.outcome = "reopened";
    }

    await updateDocument(richiesta.id, updates);

    setReopeningTicketId(null);
    setReopenSettoriSelezionati((prev) => {
      const copy = { ...prev };
      delete copy[richiesta.id];
      return copy;
    });
  };

  return (
    <div
      className="page-container"
      style={{ padding: "24px 16px", backgroundColor: "#f5f7fb" }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ marginBottom: "24px" }}>
          <h1 className="page-title" style={{ marginBottom: "4px" }}>
            Richiesta Info Documenti
          </h1>
          <p
            style={{
              margin: 0,
              marginBottom: "8px",
              color: "#555",
              maxWidth: "640px",
            }}
          >
            Crea ticket con i dati e uno screenshot delle fatture per chiedere
            ai responsabili a quale settore associare il costo.
          </p>
        </div>

        <div
          style={{
            marginBottom: "28px",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <h2 style={{ marginBottom: "16px", fontSize: "1.1rem" }}>
            Nuova richiesta
          </h2>
          <form
            onSubmit={handleSubmit}
            style={{ display: "grid", gap: "12px" }}
          >
            <div className="form-group">
              <label>Data fattura</label>
              <input
                type="date"
                value={dataFattura}
                onChange={(e) => setDataFattura(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Nome fornitore</label>
              <input
                type="text"
                value={nomeFornitore}
                onChange={(e) => setNomeFornitore(e.target.value)}
                placeholder="Es. Enel Energia"
              />
            </div>
            <div className="form-group">
              <label>Importo</label>
              <input
                type="number"
                step="0.01"
                value={importo}
                onChange={(e) => setImporto(e.target.value)}
                placeholder="Es. 1234.56"
              />
            </div>
            <div className="form-group">
              <label>Note</label>
              <textarea
                rows="3"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note aggiuntive per il responsabile"
              />
            </div>
            <div className="form-group">
              <label>Settori coinvolti</label>
              <Select
                isMulti
                options={settoreOptions}
                value={settoriSelezionati}
                onChange={(value) => setSettoriSelezionati(value || [])}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Seleziona uno o più settori"
                isClearable
                isSearchable
                menuPortalTarget={document.body}
              />
            </div>
            <div className="form-group">
              <label>Screenshot fattura</label>
              <input type="file" accept="image/*" onChange={handleFileChange} />
            </div>

            {formError && <p className="error">{formError}</p>}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn" disabled={isSaving}>
                {isSaving ? "Salvataggio..." : "Crea richiesta"}
              </button>
            </div>
          </form>
        </div>

        <div>
          <h2 style={{ marginBottom: "12px", fontSize: "1.1rem" }}>
            Richieste aperte
          </h2>
          {error && <p className="error">{error}</p>}
          {!richieste && <p>Caricamento richieste...</p>}
          {richieste && richieste.length === 0 && (
            <p>Nessuna richiesta presente.</p>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "16px",
            }}
          >
            {richieste &&
              richieste.map((r) => (
                <div
                  key={r.id}
                  style={{
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    padding: "16px",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    borderLeft:
                      r.status === "pending"
                        ? "4px solid #ffc107"
                        : "4px solid #9e9e9e",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontSize: "0.85rem", color: "#666" }}>
                      {r.data &&
                        (r.data.toDate
                          ? format(r.data.toDate(), "d MMM yyyy", {
                              locale: it,
                            })
                          : format(new Date(r.data), "d MMM yyyy", {
                              locale: it,
                            }))}
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "2px 8px",
                        borderRadius: "999px",
                        backgroundColor:
                          r.status === "pending" ? "#ffc107" : "#9e9e9e",
                        color: "#fff",
                        textTransform: "uppercase",
                      }}
                    >
                      {r.status}
                    </span>
                  </div>
                  <h3
                    style={{
                      margin: "0 0 4px",
                      fontSize: "1rem",
                      color: "#333",
                    }}
                  >
                    {r.nomeFornitore}
                  </h3>
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                  >
                    Importo: € {r.importo}
                  </p>
                  {r.note && (
                    <p style={{ margin: "0 0 8px", fontSize: "0.9rem" }}>
                      {r.note}
                    </p>
                  )}
                  {r.settori &&
                    Array.isArray(r.settori) &&
                    r.settori.length > 0 && (
                      <div style={{ margin: "4px 0 8px" }}>
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                          }}
                        >
                          Settori
                        </p>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {r.settori.map((s) => {
                            const isConfermante =
                              r.assignedSectorId && s.id === r.assignedSectorId;
                            let bg = "#bdbdbd";
                            let labelStato = s.status || "";
                            if (s.status === "pending") {
                              bg = "#ffc107";
                              labelStato = "pending";
                            } else if (s.status === "assigned") {
                              bg = "#4caf50";
                              labelStato = isConfermante
                                ? "assegnato (confermante)"
                                : "assegnato";
                            } else if (s.status === "closed-not-my-sector") {
                              bg = "#9e9e9e";
                              labelStato = "non di competenza";
                            } else if (s.status === "closed-assigned") {
                              bg = "#9e9e9e";
                              labelStato =
                                "chiuso (assegnato da altro settore)";
                            }
                            return (
                              <span
                                key={s.id}
                                style={{
                                  fontSize: "0.75rem",
                                  padding: "2px 8px",
                                  borderRadius: "999px",
                                  backgroundColor: bg,
                                  color: "#fff",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {s.nome} - {labelStato}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  {(r.projectNome || r.outcome) && (
                    <div
                      style={{
                        margin: "4px 0 8px",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        border: "1px solid #e0e6ff",
                        backgroundColor: "#f4f6ff",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: "#2f3c7e",
                        }}
                      >
                        Risposta
                      </p>
                      {r.projectNome && (
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: "0.9rem",
                            color: "#333",
                          }}
                        >
                          Cantiere assegnato:{" "}
                          {r.projectCodice
                            ? `${r.projectCodice} - ${r.projectNome}`
                            : r.projectNome}
                        </p>
                      )}
                      {r.outcome === "not-my-sector" && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.85rem",
                            color: "#555",
                          }}
                        >
                          Tutti i settori hanno indicato che il documento non è
                          di loro competenza.
                        </p>
                      )}
                      {r.outcome === "reopened" && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "0.85rem",
                            color: "#555",
                          }}
                        >
                          Richiesta riaperta per uno o più settori.
                        </p>
                      )}
                    </div>
                  )}
                  {r.fotoUrl && (
                    <div style={{ marginTop: "8px", marginBottom: "4px" }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => setModalImage(r.fotoUrl)}
                        style={{
                          backgroundColor: "#ffffff",
                          color: "#1976d2",
                          border: "1px solid #1976d2",
                          width: "100%",
                        }}
                      >
                        Apri screenshot fattura
                      </button>
                    </div>
                  )}
                  <div style={{ marginTop: "8px" }}>
                    {r.status === "pending" && (
                      <button
                        className="btn"
                        type="button"
                        onClick={() => toggleStatus(r)}
                        style={{
                          backgroundColor: "#4caf50",
                          width: "100%",
                        }}
                      >
                        Segna come chiusa
                      </button>
                    )}
                    {r.status === "closed" && (
                      <>
                        {reopeningTicketId !== r.id && (
                          <button
                            className="btn"
                            type="button"
                            onClick={() => {
                              setReopeningTicketId(r.id);
                              setReopenSettoriSelezionati((prev) => ({
                                ...prev,
                                [r.id]: [],
                              }));
                            }}
                            style={{
                              backgroundColor: "#757575",
                              width: "100%",
                            }}
                          >
                            Riapri richiesta
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleDeleteTicket(r)}
                          style={{
                            backgroundColor: "#d32f2f",
                            color: "#fff",
                            width: "100%",
                            marginTop: "6px",
                          }}
                        >
                          Elimina richiesta
                        </button>
                        {reopeningTicketId === r.id && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px",
                              borderRadius: "4px",
                              border: "1px solid #e0e0e0",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <p
                              style={{
                                margin: "0 0 6px",
                                fontSize: "0.85rem",
                                fontWeight: "bold",
                              }}
                            >
                              Seleziona i settori da riaprire
                            </p>
                            <Select
                              isMulti
                              options={
                                r.settori && Array.isArray(r.settori)
                                  ? r.settori
                                      .filter((s) => s.status !== "pending")
                                      .map((s) => ({
                                        value: s.id,
                                        label: s.nome,
                                      }))
                                  : []
                              }
                              value={reopenSettoriSelezionati[r.id] || []}
                              onChange={(value) =>
                                setReopenSettoriSelezionati((prev) => ({
                                  ...prev,
                                  [r.id]: value || [],
                                }))
                              }
                              className="react-select-container"
                              classNamePrefix="react-select"
                              placeholder="Scegli i settori"
                              isClearable
                              isSearchable
                              menuPortalTarget={document.body}
                            />
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                marginTop: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                type="button"
                                className="btn"
                                onClick={() => handleConfirmReopen(r)}
                                disabled={
                                  !reopenSettoriSelezionati[r.id] ||
                                  reopenSettoriSelezionati[r.id].length === 0
                                }
                                style={{ flex: 1, minWidth: "140px" }}
                              >
                                Conferma riapertura
                              </button>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => {
                                  setReopeningTicketId(null);
                                  setReopenSettoriSelezionati((prev) => {
                                    const copy = { ...prev };
                                    delete copy[r.id];
                                    return copy;
                                  });
                                }}
                                style={{
                                  backgroundColor: "#bdbdbd",
                                  flex: 1,
                                  minWidth: "120px",
                                }}
                              >
                                Annulla
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      {modalImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setModalImage(null)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "10px",
              borderRadius: "8px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={modalImage}
              alt="Fattura"
              style={{ maxWidth: "100%", maxHeight: "80vh", display: "block" }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => setModalImage(null)}
              style={{ marginTop: "8px", width: "100%" }}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
